var _excluded = ["name", "errorMessage", "description", "children", "mode", "type", "label", "value", "disabled", "required", "loading", "isValid", "isEmpty", "onChange", "onBlur", "onFocus"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import * as React from 'react';
import PropTypes from 'prop-types';
import { useFela } from 'react-fela';
import { objectFilter } from 'fast-loops';
import BaseInput from '../textInput/BaseInput.js';
var icon = 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0NDggNTEyIj48cGF0aCBkPSJNNDQzLjUgMTYyLjZsLTcuMS03LjFjLTQuNy00LjctMTIuMy00LjctMTcgMEwyMjQgMzUxIDI4LjUgMTU1LjVjLTQuNy00LjctMTIuMy00LjctMTcgMGwtNy4xIDcuMWMtNC43IDQuNy00LjcgMTIuMyAwIDE3bDIxMSAyMTEuMWM0LjcgNC43IDEyLjMgNC43IDE3IDBsMjExLTIxMS4xYzQuOC00LjcgNC44LTEyLjMuMS0xN3oiLz48L3N2Zz4=';
var typeToColorCodingMap = {
  default: {
    background: 'white',
    color: 'foreground.primary'
  },
  info: {
    background: 'background.info',
    color: 'foreground.info'
  },
  success: {
    background: 'background.success',
    color: 'foreground.successDark'
  },
  warning: {
    background: 'background.warning',
    color: 'foreground.warningDark'
  },
  destructive: {
    background: 'background.destructive',
    color: 'foreground.destructive'
  }
};
export default function SelectInput(_ref) {
  var name = _ref.name,
    errorMessage = _ref.errorMessage,
    description = _ref.description,
    children = _ref.children,
    mode = _ref.mode,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'default' : _ref$type,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? '' : _ref$label,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? '' : _ref$value,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$required = _ref.required,
    required = _ref$required === void 0 ? false : _ref$required,
    loading = _ref.loading,
    _ref$isValid = _ref.isValid,
    isValid = _ref$isValid === void 0 ? true : _ref$isValid,
    isEmpty = _ref.isEmpty,
    onChange = _ref.onChange,
    onBlur = _ref.onBlur,
    onFocus = _ref.onFocus,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useFela = useFela(),
    theme = _useFela.theme;
  var inputProps = objectFilter(props, function (_, key) {
    return key !== 'data-test';
  });
  var colorCode = typeToColorCodingMap[type];
  return /*#__PURE__*/React.createElement(BaseInput, {
    name: name,
    label: label,
    disabled: disabled,
    isValid: isValid,
    isEmpty: isEmpty,
    value: value,
    loading: loading,
    mode: mode,
    errorMessage: errorMessage,
    description: description,
    "data-test": props['data-test'],
    wrapperStyle: {
      backgroundImage: "url(\"data:image/svg+xml;base64,".concat(icon, "\")"),
      backgroundSize: '14px 14px',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 16px center',
      paddingRight: 20,
      extend: {
        condition: !disabled,
        style: {
          backgroundColor: colorCode.background,
          color: colorCode.color
        }
      }
    },
    inputStyle: {
      '& option': {
        appearance: 'none',
        fontFamily: theme.fonts.BODY,
        fontSize: theme.typography.spruce.fontSize,
        lineHeight: theme.typography.spruce.lineHeight,
        fontWeight: 400
      },
      extend: [{
        condition: !disabled && !loading,
        style: {
          cursor: 'pointer'
        }
      }]
    }
  }, function (innerProps) {
    return /*#__PURE__*/React.createElement("select", _extends({}, inputProps, innerProps, {
      required: required,
      onChange: onChange,
      onBlur: onBlur,
      onFocus: onFocus
    }), children);
  });
}
SelectInput.propTypes = {
  /** The change event handler. Function signature is `(newValue, event) => handler` */
  onChange: PropTypes.func.isRequired,
  /** The bur event handler */
  onBlur: PropTypes.func,
  /** The focus event handler */
  onFocus: PropTypes.func,
  /** A unique semantic name that is connected to the label */
  name: PropTypes.string.isRequired,
  /** The controlled value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Sets the validation state */
  isValid: PropTypes.bool,
  /** A function that takes the value and returns a boolean that indicates an empty state */
  isEmpty: PropTypes.func,
  /** Sets disabled */
  disabled: PropTypes.bool,
  /** Sets required */
  required: PropTypes.bool,
  /** Whether the input is loading */
  loading: PropTypes.bool,
  /** The label text */
  label: PropTypes.string,
  /** An error message text that is displayed once isValid is false */
  errorMessage: PropTypes.string,
  /** Additional description information display beneath the input */
  description: PropTypes.string,
  /** The select options */
  children: PropTypes.node,
  mode: PropTypes.oneOf(['compact']),
  /** colors to use for fore- and background */
  type: PropTypes.oneOf(['default', 'success', 'warning', 'destructive', 'info'])
};